import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listHospitals,
  deleteHospital,
  Hospital,
  HospitalListParams,
  HospitalStatus,
  OnboardingStatus,
} from "../../services/superAdmin.service";
import ConfirmModal from "../../components/common/ConfirmModal";
import {
  Building2,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import CustomSelect from "../../components/common/CustomSelect";

const STATUS_OPTIONS: { label: string; value: HospitalStatus | "" }[] = [
  { label: "All Status", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const ONBOARDING_OPTIONS: { label: string; value: OnboardingStatus | "" }[] = [
  { label: "All Onboarding", value: "" },
  { label: "Not Started", value: "NOT_STARTED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
];

const getHospitalFiltersFromSearch = (
  search: string
): { status: HospitalStatus | ""; onboardingStatus: OnboardingStatus | "" } => {
  const params = new URLSearchParams(search);
  const status = params.get("status");
  const onboardingStatus = params.get("onboardingStatus");

  return {
    status: status === "ACTIVE" || status === "INACTIVE" ? status : ("" as const),
    onboardingStatus:
      onboardingStatus === "NOT_STARTED" ||
      onboardingStatus === "IN_PROGRESS" ||
      onboardingStatus === "COMPLETED"
        ? onboardingStatus
        : ("" as const),
  };
};

const HospitalList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<HospitalStatus | "">(
    () => getHospitalFiltersFromSearch(location.search).status
  );
  const [onboardingFilter, setOnboardingFilter] = useState<OnboardingStatus | "">(
    () => getHospitalFiltersFromSearch(location.search).onboardingStatus
  );
  const [page, setPage] = useState(1);
  const limit = 10;

  // Initialize filters from URL query params (dashboard drill-down)
  useEffect(() => {
    const filters = getHospitalFiltersFromSearch(location.search);
    setStatusFilter(filters.status);
    setOnboardingFilter(filters.onboardingStatus);
  }, [location.search]);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<Hospital | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchHospitals = useCallback(async (isSearch = false) => {
    if (isSearch) setSearching(true);
    else setLoading(true);
    try {
      const params: HospitalListParams = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (onboardingFilter) params.onboardingStatus = onboardingFilter;

      const result = await listHospitals(params);
      setHospitals(result.hospitals);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      toast.error("Failed to load hospitals");
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [page, search, statusFilter, onboardingFilter]);

  useEffect(() => {
    fetchHospitals();
  }, [fetchHospitals]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteHospital(deleteTarget.id);
      toast.success(`"${deleteTarget.hospitalName}" deleted`);
      setDeleteTarget(null);
      fetchHospitals();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete hospital");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusFilterChange = (value: HospitalStatus | "") => {
    setStatusFilter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Filters & Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, email, domain, or city..."
              className="w-full h-10 pl-10 pr-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <CustomSelect
            value={statusFilter}
            onChange={(val) => handleStatusFilterChange(val as HospitalStatus | "")}
            options={STATUS_OPTIONS}
            variant="filter"
            placeholder="All Status"
            className="h-10"
          />
          <CustomSelect
            value={onboardingFilter}
            onChange={(val) => {
              setOnboardingFilter(val as OnboardingStatus | "");
              setPage(1);
            }}
            options={ONBOARDING_OPTIONS}
            variant="filter"
            placeholder="All Onboarding"
            className="h-10"
          />
          <button
            onClick={() => navigate("/hospitals/new")}
            className="flex items-center justify-center gap-2 h-10 bg-primary-600 text-white px-4 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors shrink-0"
          >
            <Plus size={18} />
            Add Hospital
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative">
        {/* Searching indicator bar */}
        {searching && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-primary-100 overflow-hidden z-10">
            <div className="h-full bg-primary-500 animate-loading-bar"></div>
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">Loading hospitals...</p>
          </div>
        ) : hospitals.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No hospitals found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search || statusFilter || onboardingFilter ? "Try adjusting your filters" : "Get started by adding a hospital"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Hospital
                    </th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Onboarding
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {hospitals.map((h) => (
                    <tr
                      key={h.id}
                      onClick={() => navigate(`/hospitals/${h.id}`)}
                      className="group hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                            <Building2 size={18} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
                              {h.hospitalName}
                            </p>
                            <p className="text-xs text-gray-400">{h.domainName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4">
                        <span className="text-sm text-gray-600 font-mono">{h.hospitalCode || "—"}</span>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4">
                        <div className="flex flex-col gap-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <Mail size={13} className="text-gray-400" />
                            {h.officialEmail}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Phone size={13} className="text-gray-400" />
                            {h.officialPhone}
                          </span>
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4">
                        <span className="flex items-center gap-1.5 text-sm text-gray-600">
                          <MapPin size={13} className="text-gray-400" />
                          {[h.city, h.country].filter(Boolean).join(", ") || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={h.status} />
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4">
                        <OnboardingBadge status={h.onboardingStatus} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/hospitals/${h.id}/edit`);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-all"
                            title="Edit hospital"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(h);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-danger-500 hover:bg-danger-50 hover:text-danger-700 transition-all"
                            title="Delete hospital"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700 font-medium px-2">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Hospital"
        message={`Are you sure you want to delete "${deleteTarget?.hospitalName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

// --- Status Badges ---

const StatusBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600";
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      classes = "bg-green-50 text-green-700 border border-green-100";
      break;
    case "INACTIVE":
      classes = "bg-gray-50 text-gray-500 border border-gray-100";
      break;
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      {status}
    </span>
  );
};

const OnboardingBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600";
  switch (status?.toUpperCase()) {
    case "COMPLETED":
      classes = "bg-green-50 text-green-700 border border-green-100";
      break;
    case "IN_PROGRESS":
      classes = "bg-blue-50 text-blue-700 border border-blue-100";
      break;
    case "NOT_STARTED":
      classes = "bg-gray-50 text-gray-500 border border-gray-100";
      break;
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

export default HospitalList;
